import { useJobAction } from "@/components/job/useJobAction";
// Field-worker form to capture a job variation: scope + customer signature.
// Used both inside JobDetail's "Variations" tab and the global /variations page.
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, FileSignature } from "lucide-react";
import { SignaturePad } from "@/components/SignaturePad";
import { toast } from "sonner";
import api from "@/lib/api";

export function VariationDialog({ job, trigger, onCreated }) {
  const [open, setOpen] = useState(false);
  useJobAction("variation", () => setOpen(true));
  const [scope, setScope] = useState("");
  const [clientName, setClientName] = useState(job?.client_name || "");
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setScope("");
    setClientName(job?.client_name || "");
    setSignature(null);
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v) reset();
  };

  const submit = async () => {
    if (!scope.trim()) return toast.error("Describe the scope of additional work");
    if (!clientName.trim()) return toast.error("Customer name is required");
    if (!signature) return toast.error("Customer must sign before submitting");
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/variations`, {
        scope: scope.trim(),
        client_name: clientName.trim(),
        client_signature: signature,
      });
      toast.success(`Variation ${data.variation_number} captured. Office has been notified.`);
      setOpen(false);
      reset();
      onCreated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save variation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger ? (
        <span onClick={() => setOpen(true)}>{trigger}</span>
      ) : (
        <Button
          onClick={() => setOpen(true)}
          className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
          data-testid="variation-new-button"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Request variation
        </Button>
      )}
      <DialogContent className="rounded-sm max-w-2xl" data-testid="variation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-[#FF5F15]" /> Variation request
          </DialogTitle>
          <DialogDescription className="text-xs">
            For <span className="font-mono font-bold">{job?.job_number || "—"}</span> · {job?.title}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="label-tag" htmlFor="var-scope">Scope of additional work</Label>
            <Textarea
              id="var-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              rows={5}
              className="mt-1.5 rounded-sm border-zinc-400 font-mono text-sm"
              placeholder="e.g. Customer asked to also remove the old culvert and reinstate the verge. Extra ~4 hours digger + 2 tonnes GAP40."
              data-testid="variation-scope-input"
            />
            <p className="text-[10px] text-zinc-400 mt-1">Office will price this up — keep it specific.</p>
          </div>
          <div>
            <Label className="label-tag" htmlFor="var-customer">Customer name</Label>
            <Input
              id="var-customer"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="mt-1.5 rounded-sm border-zinc-400"
              placeholder="Person who's authorising the extras"
              data-testid="variation-client-input"
            />
          </div>
          <div>
            <Label className="label-tag mb-1.5 block">Customer signature</Label>
            {signature ? (
              <div className="border-2 border-emerald-300 bg-emerald-50 p-3 flex items-center gap-3" data-testid="variation-sig-captured">
                <img src={signature} alt="Customer signature" className="h-16 bg-white border border-zinc-300" />
                <div className="flex-1">
                  <div className="font-bold text-sm">Signature captured</div>
                  <div className="text-[11px] text-zinc-500">By {clientName || "the customer"}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setSignature(null)}
                  className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 hover:text-[#FF5F15]"
                  data-testid="variation-sig-reset"
                >
                  Re-sign
                </button>
              </div>
            ) : (
              <SignaturePad
                onSave={(dataUrl) => setSignature(dataUrl)}
                testIdPrefix="variation-sig"
              />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="rounded-sm uppercase tracking-wider font-bold text-xs"
            data-testid="variation-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !signature}
            className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
            data-testid="variation-submit-button"
          >
            {busy ? "Submitting..." : "Submit to office"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
